"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/Button";
import { CheckIcon, ChevronRightIcon, LightBulbIcon } from "@heroicons/react/24/outline";
import CompletionCheck from "@/components/feed/post/CompletionCheck";
import { getFeedImageAssetPathAsUrl } from "@/lib/feed/assets";
import { markdownToHtml } from "@/lib/text";
import { motion } from "framer-motion";
import clsx from "clsx";
import PostImageOverlay from "@/app/(public)/learning-feed/PostImageOverlay";
import { frontendApi } from "@/lib/api-requests";
import { setChatSelection } from "@/store/reducers/chatSlice";
import { wordTapTools } from "@/lib/dictionary/word-tap-tools";
import { useDispatch, useSelector } from "react-redux";
import { XP_POINTS } from "@/lib/constants";
import { useSfxProvider } from "@/hooks/useSfxProvider";
import { useUserLocale } from "@/hooks/useUserLocale";
import { feedActions } from "@/store/actions/feed";
import { RootState } from "@/store/store";
import { PROBABILITY_OF_ONE_UP_ON_QUIZ_CORRECT_ANSWER } from "@/lib/constants-ui";
import { mergePostUserData } from "@/store/reducers/feedSlice";
import { getServerStrings } from "@/translations-server/ServerStrings";
import { getUiStringsForServer } from "@/translations-server/ServerUiStrings";

export interface StaticQuizFormProps {
    postId: string;
    quizData: { questions: { question: string; imageUrl?: string; note?: string; options: string[]; answer: number; explanation?: string; hint?: string; }[] };
    quizResponses?: { [question: string]: number };
    anchorId?: string;
    currentQuizQuestionIndex?: number;
    language: string;
    nativeLanguage: string;
}

export default function StaticQuizForm({ postId, quizData, quizResponses, currentQuizQuestionIndex = 0, anchorId, language, nativeLanguage }: StaticQuizFormProps) {
    const t = getUiStringsForServer(nativeLanguage);

    const [answers, setAnswers] = useState<number[]>(Array(quizData.questions.length).fill(-1));
    const [current, setCurrent] = useState(currentQuizQuestionIndex);
    const [interactionStarted, setInteractionStarted] = useState(false);
    const [revealed, setRevealed] = useState<boolean[]>(Array(quizData.questions.length).fill(false));
    const [imageOverlay, setImageOverlay] = useState<{ isOpen: boolean; imageUrl: string; alt: string }>({ 
        isOpen: false, 
        imageUrl: '', 
        alt: '' 
    });

    const [isRerun, setIsRerun] = useState(false);

    const isCompleted = useMemo(() => {
        return current >= quizData.questions.length - 1 && answers.every((answer) => answer !== -1);
    }, [current, quizData.questions, answers]);

    // const dispatch = useDispatch();

    const firstLoadRef = useRef(true);

    // const sfxProvider = useSfxProvider();

    const numCorrect = useMemo(() => {
        return answers.reduce((acc, val, idx) => acc + (val === quizData.questions[idx].answer ? 1 : 0), 0);
    }, [answers, quizData.questions]);

    const handleImageClick = useCallback((imageUrl: string, alt: string = "Quiz question image") => {
        setImageOverlay({ isOpen: true, imageUrl, alt });
    }, []);

    const closeImageOverlay = useCallback(() => {
        setImageOverlay({ isOpen: false, imageUrl: '', alt: '' });
    }, []);

    const select = (questionIndex: number, optionIndex: number) => {
        if (revealed[questionIndex]) return;
        const next = [...answers];
        next[questionIndex] = optionIndex;
        setAnswers(next);
        // Immediately reveal correctness for this question
        const revNext = [...revealed];
        revNext[questionIndex] = true;
        setRevealed(revNext);

        // Update post user data in the store, 
        // so that when the quiz is restored from invisible state, it resumes from the last showed question
        const updatedQuizResponses = { ...(quizResponses ?? {}), [questionIndex.toString()]: optionIndex };
        let nextCurrentQuizQuestionIndex = questionIndex + 1;

        if (nextCurrentQuizQuestionIndex >= quizData.questions.length) {
            nextCurrentQuizQuestionIndex = quizData.questions.length - 1;
        }

        // dispatch(mergePostUserData({ postId, userData: { quizResponses: updatedQuizResponses, currentQuizQuestionIndex: nextCurrentQuizQuestionIndex } }));

        const isCorrect = optionIndex === quizData.questions[questionIndex].answer;
        
        if (!isRerun) {
            // onInteract?.(isCorrect ? XP_POINTS.feed.quizQuestionAnsweredCorrectly : XP_POINTS.feed.quizQuestionAnswered);
        } else {
            // onInteract?.(0);
        }
        
        setInteractionStarted(true);
        
        if (isCorrect) {
            // sfxProvider.playSfx("/sfx/wpm-good.mp3");

            // Earn heart lives with a certain changes upon correct answer

        } else {
            // sfxProvider.playSfx("/sfx/wpm-bad.mp3");
        }

        // If the quiz is complete, call the onComplete callback
        if (questionIndex === quizData.questions.length - 1) {
            // onComplete?.();
        }
    };

    // Handle translation selection
    function handleWordContainerClick(e: any) {
        const target = e.target;
        if (!wordTapTools.isValidWordTapTarget(target)) return;
        const chatSelection = wordTapTools.getChatSelectionFromWordTap(target);
        
        if (chatSelection) {
            // dispatch(setChatSelection(chatSelection));
        }
    }

    useEffect(() => {
        // Apply previous answers, if any
        if (quizResponses) {
            const next = [...answers];
            const revealedNext = [...revealed];

            let currentIndex = 0;

            for (const [questionNum, answer] of Object.entries(quizResponses)) {
                if (answer != -1) {
                    next[parseInt(questionNum)] = answer;
                    revealedNext[parseInt(questionNum)] = true;
                    currentIndex = parseInt(questionNum);
                }
            }
            setAnswers(next);
            setRevealed(revealedNext);
            // setCurrent(currentIndex);
            firstLoadRef.current = true;
        }
    }, [quizResponses]);

    useEffect(() => {       
        if (firstLoadRef.current) {
            firstLoadRef.current = false;
            return;
        }

        // Convert answers to hashmap questionNum: answer
        const quizResponses = answers.reduce((acc, answer, index) => {
            if (answer != -1) {
                acc[index.toString()] = answer;
            }
            return acc;
        }, {} as { [questionNum: string]: number });

        frontendApi.feed.updateQuizResponses(postId, quizResponses);              
    }, [answers, firstLoadRef]);

    return (
        <div className="rounded-xl overflow-hidden">
            {/* Carousel viewport */}
            <div className="relative">
                {/* Progress dots */}
                {/* <div className="absolute top-4 left-0 right-0 flex justify-center gap-1 z-10">
                    {quizData.questions.map((_, i) => (
                        <div key={i} className={
                            "h-1.5 rounded-full transition-all " +
                            (i <= current ? "bg-blue-500 w-6" : "bg-gray-200 w-3")
                        } />
                    ))}
                </div> */}

                {/* Slide */}
                <div className="p-4 px-0 pt-4">
                    {(() => {
                        const q = quizData.questions[current];
                        const questionIndex = current;
                        const answeredIndex = answers[questionIndex];
                        const isRevealed = revealed[questionIndex];
                        const isAnsweredCorrectly = isRevealed && answeredIndex === q.answer;

                        return (
                            <div>
                                {q.imageUrl && <div className="mb-3">                                    
                                    <motion.img
                                        src={getFeedImageAssetPathAsUrl(q.imageUrl)}
                                        alt={q.question}
                                        className="w-full max-w-sm mx-auto h-auto rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ duration: 0.38, ease: "easeInOut" }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleImageClick(getFeedImageAssetPathAsUrl(q.imageUrl), q.question);
                                        }}
                                    />
                                </div>}
                                <div className="text-base font-semibold wtc px-4" dangerouslySetInnerHTML={{ __html: formatTextContent(q.question) }}></div>
                                {q.note && <div className="text-xs text-gray-500 mt-1 px-4 wtc" dangerouslySetInnerHTML={{ __html: formatTextContent(q.note) }}></div>}
                                <div className="mt-3 grid grid-cols-1 gap-2 px-1">
                                    {q.options.map((opt, optionIndex) => {
                                        const isSelected = answeredIndex === optionIndex;
                                        const isCorrect = isRevealed && optionIndex === q.answer;
                                        const isWrongSelected = isRevealed && isSelected && optionIndex !== q.answer;
                                        const ring = isSelected ? "ring-2 ring-blue-400 " : "";
                                        return (
                                            <button
                                                key={optionIndex}
                                                className={
                                                    "group relative w-full text-left px-3 py-2 rounded-lg border transition-colors " +
                                                    (isCorrect ? "border-green-500 bg-green-50 " : isWrongSelected ? "border-red-500 bg-red-50 " : "border-gray-200 hover:border-gray-300 ") +
                                                    ring
                                                }
                                                onClick={() => {
                                                    if (isRevealed) {
                                                        if (current < quizData.questions.length - 1) {
                                                            setCurrent((c) => Math.min(quizData.questions.length - 1, c + 1))
                                                            // dispatch(mergePostUserData({ postId, userData: { currentQuizQuestionIndex: Math.min(quizData.questions.length - 1, current + 1) } }));
                                                        }
                                                    } else {
                                                        select(questionIndex, optionIndex)
                                                    }
                                                }}                                                
                                            >
                                                <span className="pr-7">{opt}</span>
                                                {isRevealed && (
                                                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xl">
                                                        {optionIndex === q.answer ? "✅" : (isSelected ? "❌" : "")}
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                                {isRevealed && (
                                    <div className="mt-3 text-sm">
                                        <div className="font-medium text-green-700">{t.quizForm.correct} {q.options[q.answer]}</div>
                                        {q.explanation && <div className="text-gray-700 mt-1">{q.explanation}</div>}
                                        {q.hint && !isAnsweredCorrectly && <div className="text-gray-500 mt-1">
                                            <LightBulbIcon className="w-4 h-4 -mt-0.5 inline-block text-yellow-500" />
                                            <span className="ml-1">{q.hint}</span>                                             
                                        </div>}
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </div>

                {/* Completion overlay */}
                {isCompleted && (
                    <div className="absolute inset-0 bg-white bg-opacity-70 backdrop-blur-sm z-10 flex flex-col items-center justify-center gap-4">
                        <div className="text-2xl font-bold text-gray-400">{t.quizForm.quizCompleted}</div>
                        <div className="w-3/4 mx-auto text-sm text-gray-400">{t.quizForm.quizCompletedDetail}</div>
                        <Button color="lushPrimary" onClick={() => {
                            // dispatch(mergePostUserData({ postId, userData: { currentQuizQuestionIndex: 0, quizResponses: {} } }));
                            setAnswers(Array(quizData.questions.length).fill(-1));
                            setRevealed(Array(quizData.questions.length).fill(false));
                            setCurrent(0);
                            setIsRerun(true);
                        }}>
                            {t.quizForm.repeatQuiz}
                        </Button>                    
                    </div>
                )}
            </div>

            {/* Footer controls */}
            <div className="px-4 py-3 flex items-center justify-between bg-gray-50 border-t border-gray-100 h-14">
                <div className="text-sm text-gray-600">
                    {t.quizForm.correctCount} {numCorrect}/{quizData.questions.length}
                </div>
                <div className="flex items-center gap-2">
                    {/* <Button
                        color="lushSecondary"
                        isDisabled={current === 0}
                        onClick={() => setCurrent((c) => Math.max(0, c - 1))}
                    >
                        Prev
                    </Button> */}
                    {(current < quizData.questions.length - 1) && interactionStarted && <Button
                        color="lushPrimary"
                        isDisabled={!revealed[current]}
                        onClick={() => setCurrent((c) => Math.min(quizData.questions.length - 1, c + 1))}
                    >
                        <ChevronRightIcon className="w-4 h-4" />
                    </Button>}

                    {current === quizData.questions.length - 1 && answers.every((answer) => answer !== -1) && 
                        <CompletionCheck className="w-7 h-7" color={clsx(
                            numCorrect === quizData.questions.length && "bg-green-500",
                            numCorrect > 0 && numCorrect < quizData.questions.length && "bg-yellow-400",
                            numCorrect === 0 && "bg-red-500",
                        )} />}
                </div>
            </div>
            
            {/* Image Overlay */}
            <PostImageOverlay
                imageUrl={imageOverlay.imageUrl}
                alt={imageOverlay.alt}
                isOpen={imageOverlay.isOpen}
                onClose={closeImageOverlay}
            />            
        </div>
    );
}

function formatTextContent(content: string): string  {   
    // Use regexp to detect markdown formatting, like **, ~~ or ## in the text
    if (content.match(/^[*_~#]+/)) {
        return markdownToHtml(content);
    } else if (content.indexOf("</") == -1) {
        return content.replaceAll("\n", "<br />");
    } else {
        return content;
    }   
}